// Rotation deviation panel — Phase 2A.5.
//
// For each gate-CD-bound priority ability (BT 6s, WW 10s), shows:
//  - actual cast count
//  - "wasted" seconds = sum of (gap_between_casts − cd) when gap > cd
//  - average and max gap between casts
//
// "Wasted" is the most actionable metric: it's the cumulative time the
// ability sat off-CD waiting for the player. Causes vary (rage starvation,
// movement, target switching) but the number itself answers "should I have
// pressed this more often?".

import type { RotationDeviation, RotationGate } from '@shared/analysis';
import { t } from '@shared/i18n';
import { iconNameToUrl, lookup } from './wowhead';
import { attachTooltip } from './tooltip';

const ICON_BASE = '/api/img/images/wow/icons/medium';

function fmtSec(s: number | null): string {
  if (s == null || !Number.isFinite(s)) return '–';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

// Idle-flavour: paint red when wasted > 30s for BT (≈5 lost casts) or > 50s
// for WW (≈5 lost casts). The thresholds are heuristic; users can read the
// raw number if they disagree.
function flavour(wasted: number, cdSec: number): string {
  const lostCasts = wasted / cdSec;
  if (lostCasts >= 5) return 'bad';
  if (lostCasts <= 1) return 'good';
  return '';
}

function makeRow(g: RotationGate, isAggregate: boolean): HTMLTableRowElement {
  const tr = document.createElement('tr');
  const lost = g.cdSec > 0 ? g.wastedSec / g.cdSec : 0;
  const avg = isAggregate ? '–' : fmtSec(g.avgGapSec);
  tr.innerHTML = `
    <td class="ability">
      <a class="rot-link" target="_blank" rel="noopener" href="/api/wh/classic/spell=${g.abilityID}">
        <span class="icon-slot"></span>
        <span class="rot-name">spell ${g.abilityID}</span>
      </a>
    </td>
    <td class="num muted">${g.cdSec}s</td>
    <td class="num">${g.uses}</td>
    <td class="num ${flavour(g.wastedSec, g.cdSec)}">${fmtSec(g.wastedSec)} <span class="muted small">≈ ${lost.toFixed(1)} casts</span></td>
    <td class="num muted">${avg}</td>
    <td class="num muted">${fmtSec(g.maxGapSec)}</td>
  `;

  const link = tr.querySelector<HTMLAnchorElement>('a.rot-link');
  if (link) attachTooltip(link, 'spell', g.abilityID);

  void lookup('spell', g.abilityID).then((r) => {
    if (r.name) {
      const n = tr.querySelector<HTMLSpanElement>('.rot-name');
      if (n) n.textContent = r.name;
    }
    const slot = tr.querySelector<HTMLSpanElement>('.icon-slot');
    if (!slot) return;
    const iconSrc = r.icon ? iconNameToUrl(r.icon) : `${ICON_BASE}/inv_misc_questionmark.jpg`;
    const img = document.createElement('img');
    img.className = 'icon';
    img.width = 20;
    img.height = 20;
    img.alt = '';
    img.src = iconSrc;
    img.addEventListener('error', () => { img.style.visibility = 'hidden'; }, { once: true });
    slot.replaceChildren(img);
  });

  return tr;
}

export function renderRotationPanel(
  rot: RotationDeviation,
  container: HTMLElement,
  isAggregate: boolean,
): void {
  container.replaceChildren();
  const usedAtAll = rot.gates.some((g) => g.uses > 0);
  if (!usedAtAll) {
    const p = document.createElement('p');
    p.className = 'muted';
    p.textContent = t('rotation.empty');
    container.appendChild(p);
    return;
  }

  const totalWasted = rot.gates.reduce((acc, g) => acc + g.wastedSec, 0);
  const summary = document.createElement('p');
  summary.className = 'analysis-summary muted';
  summary.textContent = t('rotation.summary', { wasted: fmtSec(totalWasted) });
  container.appendChild(summary);

  const table = document.createElement('table');
  table.className = 'rot-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>${t('rotation.col.ability')}</th>
        <th>${t('rotation.col.cd')}</th>
        <th>${t('rotation.col.uses')}</th>
        <th title="${escapeHtml(t('rotation.col.wastedTip'))}">${t('rotation.col.wasted')}</th>
        <th title="${escapeHtml(t('rotation.col.avgTip'))}">${t('rotation.col.avg')}</th>
        <th title="${escapeHtml(t('rotation.col.maxTip'))}">${t('rotation.col.max')}</th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody')!;
  for (const g of rot.gates) tbody.appendChild(makeRow(g, isAggregate));
  container.appendChild(table);
}
